import { ReactNode } from 'react';
import {
  createTheme,
  Palette,
  PaletteColor,
  Theme,
  ThemeProvider,
} from '@mui/material';
import { deepmerge } from '@mui/utils';

interface CustomPaletteColor extends PaletteColor {
  lightest: string;
  lighter: string;
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    xlHeadline: React.CSSProperties;
    body: React.CSSProperties;
    bodyBlack: React.CSSProperties;
    bodyLarge: React.CSSProperties;
    bodyLargeBold: React.CSSProperties;
    bodyLargeBlack: React.CSSProperties;
    bodySmall: React.CSSProperties;

    title: React.CSSProperties;
    titleSmall: React.CSSProperties;
    textLargeBold: React.CSSProperties;
    textLarge: React.CSSProperties;
    textBold: React.CSSProperties;
    text: React.CSSProperties;
    textSmall: React.CSSProperties;
    textExtraSmall: React.CSSProperties;
    accent: React.CSSProperties;
    accentSmall: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    xlHeadline?: React.CSSProperties;
    body?: React.CSSProperties;
    bodyBlack?: React.CSSProperties;
    bodyLarge?: React.CSSProperties;
    bodyLargeBold?: React.CSSProperties;
    bodyLargeBlack?: React.CSSProperties;
    bodySmall?: React.CSSProperties;

    title?: React.CSSProperties;
    titleSmall?: React.CSSProperties;
    textLargeBold?: React.CSSProperties;
    textLarge?: React.CSSProperties;
    textBold?: React.CSSProperties;
    text?: React.CSSProperties;
    textSmall?: React.CSSProperties;
    textExtraSmall?: React.CSSProperties;
    accent?: React.CSSProperties;
    accentSmall?: React.CSSProperties;
  }

  interface PaletteOptions {
    blue: CustomPaletteColor;
    green: CustomPaletteColor;
    yellow: CustomPaletteColor;
    red: CustomPaletteColor;
    neutral: CustomPaletteColor;
  }

  interface Palette {
    blue: CustomPaletteColor;
    green: CustomPaletteColor;
    yellow: CustomPaletteColor;
    red: CustomPaletteColor;
    neutral: CustomPaletteColor;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    xlHeadline: true;
    body: true;
    bodyBlack: true;
    bodyLarge: true;
    bodyLargeBold: true;
    bodyLargeBlack: true;
    bodySmall: true;
    body1: false;
    body2: false;
    button: false;
    caption: false;
    overline: false;
    subtitle1: false;
    subtitle2: false;

    title: true;
    titleSmall: true;
    textLargeBold: true;
    textLarge: true;
    textBold: true;
    text: true;
    textSmall: true;
    textExtraSmall: true;
    accent: true;
    accentSmall: true;
  }
}

export const GOTHAM = `Gotham, system-ui, -apple-system, "system-ui", "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Droid Sans", "Helvetica Neue", sans-serif`;
export const GOTHAM_DISPLAY = `var(--font-gotham), Gotham Display, sans-serif`;
export const CHRONICLE_DISPLAY = `Chronicle Display, sans-serif`;

export const { breakpoints, palette } = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
    },
  },
});

Object.assign(palette, {
  text: {
    primary: '#000',
    secondary: '#64666A',
  },
  primary: {
    dark: '#006642',
    main: '#008254',
    light: '#40AD87',
  },
  secondary: {
    light: '#EEF7FF',
    main: '#005FB9',
  },
  common: {
    white: '#ffffff',
  },
  grey: {
    '100': '#f7f7f7',
    '200': '#D8D9DA',
    '300': '#B1B3B5',
    '400': '#8B8C8F',
    '500': '#64666A',
    '800': '#262626',
  },

  /* NerdWallet brand colors */
  blue: {
    lightest: '#EEF7FF',
    lighter: '#9DCDFA',
    light: '#54A0E8',
    main: '#005FB9',
    dark: '#02427E',
  },
  green: {
    lightest: '#E3FAF2',
    lighter: '#B7ECDA',
    light: '#40AD87',
    main: '#008255',
    dark: '#006642',
  },
  yellow: {
    lightest: '#FFF7EA',
    lighter: '#FFE2AE',
    light: '#FBD083',
    main: '#FFC764',
    dark: '#EFA92E',
  },
  red: {
    lightest: '#FFE8E8',
    lighter: '#FF9090',
    light: '#FF6868',
    main: '#DE2828',
    dark: '#B40000',
  },
  neutral: {
    lightest: '#F5F5F5',
    lighter: '#D8D9DA',
    light: '#B1B3B5',
    main: '#8B8C8F',
    dark: '#64666A',
  },
} as Palette);

declare module '@mui/material/ButtonGroup' {
  interface ButtonGroupPropsVariantOverrides {
    quickOptions: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    creditScoreWidget: true;
    blueBordered: true;
    blueBorderedLighter: true;
  }
}

export const theme = createTheme({
  palette,
  breakpoints,
  spacing: (size: number) => {
    switch (size) {
      case 0:
        return 0;
      case 1:
        return '0.25rem';
      case 2:
        return '0.5rem';
      case 3:
        return '1rem';
      case 3.5:
        return '1.5rem';
      case 4:
        return '1.75rem';
      case 5:
        return '2.75rem';
      default:
        return `${0.5 * size}rem`;
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
				:root {
					--color-black: #000;
					--color-green: ${palette.primary.main};
					--color-green130: ${palette.primary.dark};
					--color-body: var(--color-black);
					--button-bg: var(--color-green);
					--form-control-text: var(---color-body);
					--form-control-trim: #b4b9c1;
				}
				body, input, select, button {
					font-size: 16px;
				}
				@font-face {
					font-family: 'Gotham';
					font-style: normal;
					font-weight: 400;
					font-display: swap;
					src: local('Gotham'), url(https://www.nerdwallet.com/cdn/fonts/Gotham-Book--critical.fdbad282be.woff2) format('woff2')
				}
				@font-face {
					font-family: 'Gotham';
					font-style: normal;
					font-weight: 500;
					font-display: swap;
					src: local('Gotham'), url(https://www.nerdwallet.com/cdn/fonts/Gotham-Medium--critical.ee5c613487.woff2) format('woff2')
				}
				@font-face {
					font-family: 'Gotham';
					font-style: normal;
					font-weight: 700;
					font-display: swap;
					src: local('Gotham'), url(https://www.nerdwallet.com/cdn/fonts/Gotham-Bold--critical.dcf83fb890.woff2) format('woff2')
				}
				@font-face {
					font-family: 'Chronicle Display';
					font-style: normal;
					font-weight: 400;
					font-display: swap;
					src: local('Chronicle Display'), url(https://www.nerdwallet.com/cdn/fonts/ChronicleDisplay-Roman--critical.835fdb1566.woff2) format('woff2')
				}
				@font-face {
					font-family: 'Chronicle Display';
					font-style: normal;
					font-weight: 600;
					font-display: swap;
					src: local('Chronicle Display'), url(https://www.nerdwallet.com/cdn/fonts/ChronicleDisplay-Semibold--critical.2c31edcaf3.woff2) format('woff2')
				}
			`,
    },
    MuiBreadcrumbs: {
      defaultProps: {
        separator: '›',
      },
      styleOverrides: {
        li: {
          fontSize: 10,
          textTransform: 'uppercase',
          fontWeight: 700,
          '& .MuiIconButton-root': {
            color: palette.green.dark,
            '&:hover': {
              backgroundColor: palette.green.lightest,
            },
          },
          '& > a': {
            color: palette.text.secondary,
            '&:hover': {
              color: palette.green.dark,
            },
          },
          '&:last-of-type': {
            color: palette.text.primary,
          },
        },
        separator: {
          marginLeft: 16,
          marginRight: 16,
          fontSize: 14,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        fullWidth: true,
        variant: 'contained',
      },
      styleOverrides: {
        root: ({ ownerState }) => {
          switch (ownerState.variant) {
            case 'contained':
              return {
                '&.Mui-disabled': {
                  background: palette.primary.main,
                  color: palette.common.white,
                  opacity: 0.5,
                },
              };
            case 'outlined':
              return {
                '&.Mui-disabled': {
                  background: palette.common.white,
                  color: palette.primary.main,
                  borderColor: palette.primary.main,
                  opacity: 0.5,
                },
              };
            default:
              return;
          }
        },
        contained: {
          fontSize: 14,
          lineHeight: 1,
          textAlign: 'center',
          padding: 16,
          textTransform: 'uppercase',
          fontWeight: 'bold',
          borderRadius: 2,
          transitionDuration: '.15s',
        },
        outlined: {
          fontSize: 14,
          lineHeight: 1,
          textAlign: 'center',
          padding: 16,
          textTransform: 'uppercase',
          fontWeight: 'bold',
          borderRadius: 2,
          transitionDuration: '.15s',
          backgroundColor: palette.common.white,
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: 'xl',
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          color: palette.secondary.main,
          cursor: 'pointer',
        },
        /*
          Setting a Link to component="button" removes our custom font,
          this is a MUI issue which is discussed here: https://github.com/mui/material-ui/issues/32942
        */
        button: {
          fontFamily: 'inherit',
        },
      },
    },

    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiCircularProgress: {
      defaultProps: {
        thickness: 2.5,
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: palette.neutral.main,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderTopLeftRadius: '0.125rem',
          borderBottomLeftRadius: '0.125rem',
          height: 6,
        },
        colorPrimary: {
          backgroundColor: palette.neutral.lightest,
        },
        barColorPrimary: {
          backgroundColor: palette.green.light,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#fff',
          color: palette.text.primary,
          fontSize: 13,
          fontWeight: 'normal',
          border: `1px solid ${palette.neutral.light}`,
          boxShadow:
            '0 4px 8px 0 rgb(100 102 106 / 15%), 0 8px 16px 0 rgb(100 102 106 / 33%)',
        },
        arrow: {
          color: '#fff',
          '&::before': {
            border: `1px solid ${palette.neutral.light}`,
            boxSizing: 'border-box',
          },
        },
      },
    },
    MuiButtonGroup: {
      variants: [
        {
          props: { variant: 'quickOptions' },
          style: {
            display: 'flex',
            flexWrap: 'wrap',
            '& .MuiButtonGroup-grouped': {
              padding: 12,
              borderRadius: '0.125rem',
              ':not(:last-of-type)': {
                borderRadius: '0.125rem',
              },
              ':not(:first-of-type)': {
                borderRadius: '0.125rem',
              },
            },
          },
        },
      ],
    },
    MuiChip: {
      styleOverrides: {
        filled: {
          background: palette.grey[100],
        },
      },
      variants: [
        {
          props: { variant: 'blueBordered' },
          style: {
            border: '0.5px solid',
            borderColor: palette.blue.dark,
            color: palette.blue.dark,
            backgroundColor: palette.blue.lightest,
          },
        },
      ],
    },
  },
  typography: {
    fontFamily: GOTHAM,
    allVariants: {
      fontFamily: GOTHAM,
      WebkitFontSmoothing: 'auto',
    },
    xlHeadline: {
      fontFamily: CHRONICLE_DISPLAY,
      fontSize: 36,
      fontWeight: 400,
      lineHeight: 1.1,
      [breakpoints.up('md')]: {
        fontSize: 54,
      },
    },
    h1: {
      fontFamily: CHRONICLE_DISPLAY,
      fontSize: 32,
      fontWeight: 400,
      [breakpoints.up('md')]: {
        fontSize: 36,
      },
    },
    h2: {
      fontSize: 24,
      lineHeight: 1.3,
      fontWeight: 500,
    },
    h3: {
      fontSize: 20,
      lineHeight: 1.3,
      fontWeight: 400,
    },
    h4: {
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 500,
    },
    h5: {
      fontFamily: CHRONICLE_DISPLAY,
    },
    h6: {
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 1,
    },
    body: {
      fontSize: 16,
      lineHeight: 1.5,
    },
    bodyBlack: {
      fontSize: 16,
      fontWeight: 700,
      lineHeight: 1.5,
    },
    bodyLarge: {
      fontSize: 20,
      lineHeight: 1.5,
      [breakpoints.up('md')]: {
        fontSize: 24,
      },
    },
    bodyLargeBold: {
      fontSize: 20,
      lineHeight: 1.3,
      fontWeight: 500,
      [breakpoints.up('md')]: {
        fontSize: 24,
      },
    },
    bodyLargeBlack: {
      fontSize: 20,
      lineHeight: 1.3,
      fontWeight: 700,
      [breakpoints.up('md')]: {
        fontSize: 24,
      },
    },
    bodySmall: {
      fontSize: 13,
      lineHeight: 1.5,
    },

    /* Currency Type styles */
    title: {
      fontSize: 32,
      lineHeight: '111%',
      fontFamily: CHRONICLE_DISPLAY,
      fontWeight: 400,
      [breakpoints.up('md')]: {
        fontSize: 48,
        lineHeight: '53px',
      },
    },
    titleSmall: {
      fontSize: 24,
      lineHeight: '117%',
      fontFamily: CHRONICLE_DISPLAY,
      fontWeight: 400,
      [breakpoints.up('md')]: {
        fontSize: 36,
        lineHeight: '42px',
      },
    },
    textLargeBold: {
      fontSize: 24,
      lineHeight: '32px',
      fontWeight: 500,
    },
    textLarge: {
      fontSize: 18,
      lineHeight: '133%',
      fontWeight: 300,
      [breakpoints.up('md')]: {
        fontSize: 20,
        lineHeight: '32px',
      },
    },
    textBold: {
      fontSize: 16,
      lineHeight: '150%',
      fontWeight: 500,
    },
    text: {
      fontSize: 16,
      lineHeight: '150%',
      fontWeight: 300,
    },
    textSmall: {
      fontSize: 13,
      lineHeight: 1.4,
      fontFamily: GOTHAM,
    },
    textExtraSmall: {
      fontSize: 11,
      lineHeight: 1.5,
      fontWeight: 400,
      letterSpacing: 0.64,
      WebkitFontSmoothing: 'auto',
    },
    accent: {
      fontSize: 14,
      lineHeight: '15px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.8125px',
    },
    accentSmall: {
      fontSize: 12,
      lineHeight: '18px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.8125px',
    },
  },
});

export const NerdWalletThemeProvider = ({
  merge,
  ...props
}: {
  merge?: Partial<Theme>;
  children?: ReactNode;
}) => <ThemeProvider theme={deepmerge(theme, merge)} {...props} />;
